import { Container, Box, Text, Title, Stack, Flex } from '@mantine/core';
import { Compare } from '@gfazioli/mantine-compare';
import { ReactNode } from 'react';

interface ProblemSolution {
  problem: string;
  solution: string;
}

const JumboTitle = ({
  children,
  order = 1,
  fz = 'xl',
  ...otherProps
}: {
  children: ReactNode;
  order?: 1 | 2 | 3 | 4 | 5;
  fz?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any;
}) => {
  const FONT_WEIGHT_MAP: Record<string, number> = {
    xl: 800,
    lg: 800,
    md: 700,
    sm: 600,
    xs: 500,
  };

  const FONT_SIZE_MAP: Record<string, string> = {
    xl: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
    lg: 'clamp(2rem, 4vw + 1rem, 3.5rem)',
    md: 'clamp(1.75rem, 3vw + 1rem, 3rem)',
    sm: 'clamp(1.5rem, 2.5vw + 1rem, 2.5rem)',
    xs: 'clamp(1.25rem, 2vw + 1rem, 2rem)',
  };

  const { style: otherStyle, ...restProps } = otherProps;

  return (
    <Title
      fw={FONT_WEIGHT_MAP[fz]}
      lh={1}
      order={order}
      style={{
        fontSize: FONT_SIZE_MAP[fz],
        ...otherStyle,
      }}
      {...restProps}
    >
      {children}
    </Title>
  );
};

const PROBLEMS_AND_SOLUTIONS: ProblemSolution[] = [
  {
    problem: 'My existing law practice software is too clunky!',
    solution:
      "Practist works from day one with minimal setup. Don't know how to do something in Practist? Just have the AI assistant do it for you.",
  },
  {
    problem: 'Keeping track of time is too hard.',
    solution:
      'Track time your way—timer, notes, or just tell the AI what you did. It behaves like a human assistant who already knows the system.',
  },
  {
    problem:
      'Too many software tools to keep track of, requiring too much training.',
    solution:
      'Practist replaces your patchwork with one system. Your team needs less training because the software is intuitive — and AI fills the gaps.',
  },
  {
    problem: "It's too hard to keep track of flat fees.",
    solution:
      'Practist makes it automatic. Just set the fee type and Practist automatically adjusts the engagement letter and allows you to mark fees earned with one click.',
  },
  {
    problem: "I can't make enough money from legal insurance.",
    solution:
      'Admin efficiency is the key to profit. With Practist, you can find a case, accept a case, and file a claim with just a few clicks.',
  },
  {
    problem: 'Hidden credit card fees.',
    solution:
      'Unlike some competitors, Practist doesn\'t charge "card brand network fees" — fees that are charged in addition to credit card processing fees.',
  },
  {
    problem: 'Everything I want is on the upgraded plan.',
    solution:
      'Practist has one plan. One affordable price includes everything.',
  },
  {
    problem: 'Training staff takes too much of my time.',
    solution:
      'Practist provides a platform for offering robust training and resources to your team. And think of our AI as another pretrained teammate.',
  },
];

const BeforeAndAfter = () => {
  return (
    <Box
      id='solutions'
      style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, var(--mantine-color-gray-2) 1px, transparent 0)`,
        backgroundSize: '8px 8px',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      <Container
        size='lg'
        py={{
          base: 'calc(var(--mantine-spacing-lg) * 4)',
          xs: 'calc(var(--mantine-spacing-lg) * 5)',
          lg: 'calc(var(--mantine-spacing-lg) * 6)',
        }}
      >
        <Stack gap='xl'>
          <Stack align='center' gap='xs'>
            <JumboTitle
              order={2}
              fz='md'
              ta='center'
              style={{ textWrap: 'balance' }}
            >
              Before and After
            </JumboTitle>
            <Text
              c='dimmed'
              ta='center'
              fz='lg'
              style={{ textWrap: 'balance' }}
            >
              See how Practist turns your problems into solutions.
            </Text>
          </Stack>

          <Flex
            gap='calc(var(--mantine-spacing-xl) * 2)'
            wrap='wrap'
            justify='center'
            style={{ width: '100%' }}
          >
            {PROBLEMS_AND_SOLUTIONS.map((item, index) => (
              <Box
                key={index}
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  overflow: 'hidden',
                  height: '250px',
                  width: '350px',
                }}
              >
                <Compare
                  variant='drag'
                  defaultPosition={95}
                  style={{ height: '100%' }}
                  leftSection={
                    <Box
                      p='xl'
                      style={{
                        background:
                          'linear-gradient(135deg, var(--mantine-color-pink-7) 0%, var(--mantine-color-pink-4) 100%)',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        overflow: 'visible',
                      }}
                    >
                      <Text fw={700} size='xl' mb='lg' c='white'>
                        The Problem
                      </Text>
                      <Text
                        size='md'
                        c='white'
                        style={{ lineHeight: 1.8, maxWidth: '90%' }}
                      >
                        {item.problem}
                      </Text>
                    </Box>
                  }
                  rightSection={
                    <Box
                      p='xl'
                      style={{
                        background:
                          'linear-gradient(135deg, var(--mantine-color-practistBlue-9) 0%, var(--mantine-color-practistBlue-3) 100%)',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        overflow: 'visible',
                      }}
                    >
                      <Text fw={700} size='xl' mb='lg' c='white'>
                        The Solution
                      </Text>
                      <Text
                        size='md'
                        c='white'
                        style={{ lineHeight: 1.8, maxWidth: '90%' }}
                      >
                        {item.solution}
                      </Text>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Flex>
        </Stack>
      </Container>
    </Box>
  );
};

export default BeforeAndAfter;
